import React, { useContext } from "react";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { COREButton } from "../../Action/COREButton";
import { COREIcon, COREIconProps } from "../../Content/COREIcon";
import { CORETooltip } from "../../Overlay/CORETooltip";
import { COREMenuItem } from "../../Navigation/COREMenuItem";
import {
  COREDropdownMenu,
  DropdownMenuItem,
} from "../../Form/COREDropdownMenu";
import { InteractionProps } from "../../Layout/COREAccordionItem";
import { FullScreenModeContext } from "../../../shared/state/fullScreenModeContext";
import { TestID } from "../../../shared/testids/testids";

/**
 * One thing a chart panel can do, declared once and rendered two ways: as an
 * icon button in the panel header (`toInteractionButton`) and as a row in a
 * dropdown (`toMenuItem`).
 *
 * This replaces three separate builders that each listed the same actions —
 * the header buttons and the dropdown in ChartHOC, and the dashboard widget
 * dropdown in WidgetPanel — and had already drifted apart ("Download CSV" vs
 * "Download as CSV", "Reset Chart" vs "Reset chart").
 */
type ChartActionBase = {
  key: "csv" | "png" | "fullScreen" | "reset" | "editInChartBuilder";
  /** Dropdown copy. */
  label: string;
  icon: COREIconProps["icon"];
  /**
   * Test id suffixes are load bearing — cypress specs select these exact
   * strings — so they live on the action rather than being spelled out again
   * at each render site.
   */
  menuTestIDSuffix: string;
};

/**
 * Where the action sits while the header still has room for a button row:
 * "header" gets its own icon button, "overflow" lives in the dropdown. Once the
 * header is too narrow the container hides the button row and collapses
 * everything into its own dropdown, at which point the split stops applying.
 *
 * Only a header action is ever drawn as a button, so only it carries a tooltip
 * and the button's test ids. Hanging them off every action instead invites
 * copy for controls that do not exist — which is what happened, leaving four
 * actions with three unreachable strings apiece. Reset's button and tooltip
 * suffixes disagree for historical reasons, hence two separate fields.
 */
type ChartActionPlacement =
  | {
      placement: "header";
      /** Tooltip for the header button, which is icon-only. */
      tooltip: string;
      buttonTestIDSuffix: string;
      tooltipTestIDSuffix: string;
    }
  | { placement: "overflow" };

/**
 * An action either does something where it stands or goes somewhere else, never
 * both and never neither — a menu row that does nothing at all is what this
 * shape rules out.
 *
 * Going somewhere is a route rather than a click handler because a control that
 * navigates has to render an anchor, or the browser cannot offer "open in new
 * tab" on it (see .agent/rules/navigation-link-patterns.md).
 */
type ChartActionTarget =
  | { onClick: () => void; to?: never }
  | { to: string; onClick?: never };

export type ChartAction = ChartActionBase &
  ChartActionPlacement &
  ChartActionTarget;

/** The one shape `toInteractionButton` can render. */
export type HeaderChartAction = ChartActionBase &
  Extract<ChartActionPlacement, { placement: "header" }> &
  ChartActionTarget;

/** Narrows the list `buildChartActions` returns down to the button row. */
export const isHeaderAction = (
  action: ChartAction
): action is HeaderChartAction => action.placement === "header";

type ChartActionHandlers = {
  onDownloadCSV?: () => void;
  onDownloadPNG?: () => void;
  /**
   * Omitted by containers that cannot expand yet — dashboard widgets, whose
   * grid transforms every tile.
   */
  onToggleFullScreen?: () => void;
  isFullScreen?: boolean;
  onResetChart?: () => void;
  /**
   * The saved chart's id, supplied only by charts the user built in Chart
   * Builder. Every other chart leaves it out and so is offered no way back to
   * an editor it never came from.
   */
  editInChartBuilderId?: number;
};

/**
 * Built in the order the Figma dropdown lists them, so ordering is decided
 * here rather than at each call site. Omitting a handler omits the action,
 * which is how a caller says its chart does not support it.
 */
export const buildChartActions = ({
  onDownloadCSV,
  onDownloadPNG,
  onToggleFullScreen,
  isFullScreen = false,
  onResetChart,
  editInChartBuilderId,
}: ChartActionHandlers): ChartAction[] => {
  const actions: ChartAction[] = [];

  if (onDownloadCSV) {
    actions.push({
      key: "csv",
      label: "Download as CSV",
      icon: icon({ name: "file-csv", style: "regular" }),
      onClick: onDownloadCSV,
      placement: "overflow",
      menuTestIDSuffix: "-download-as-csv-menu",
    });
  }

  if (onDownloadPNG) {
    actions.push({
      key: "png",
      label: "Download as PNG",
      icon: icon({ name: "image", style: "regular" }),
      onClick: onDownloadPNG,
      placement: "overflow",
      menuTestIDSuffix: "-download-as-png-menu",
    });
  }

  if (onToggleFullScreen) {
    // One toggling action, so the way out of full screen is the control that
    // got you in.
    actions.push({
      key: "fullScreen",
      label: isFullScreen ? "Exit full screen" : "Full screen",
      icon: isFullScreen
        ? icon({ name: "compress", style: "regular" })
        : icon({ name: "expand", style: "regular" }),
      onClick: onToggleFullScreen,
      placement: "overflow",
      menuTestIDSuffix: "-full-screen-menu",
    });
  }

  if (onResetChart) {
    actions.push({
      key: "reset",
      label: "Reset chart",
      tooltip: "Reset chart",
      icon: icon({ name: "undo", style: "regular" }),
      onClick: onResetChart,
      placement: "header",
      buttonTestIDSuffix: "-relayout",
      tooltipTestIDSuffix: "-reset-tooltip",
      menuTestIDSuffix: "-reset-chart-menu",
    });
  }

  if (editInChartBuilderId !== undefined) {
    actions.push({
      key: "editInChartBuilder",
      label: "Edit in Chart Builder",
      icon: icon({ name: "pen-to-square", style: "regular" }),
      to: `/chart-builder?chartConfigId=${editInChartBuilderId}`,
      placement: "overflow",
      menuTestIDSuffix: "-edit-in-chart-builder-menu",
    });
  }

  return actions;
};

/**
 * The placement is read from the context rather than passed in, so every caller
 * of `toInteractionButton` is covered without having to know about it.
 */
const ChartActionButton: React.FC<{
  action: HeaderChartAction;
  testID: TestID;
}> = ({ action, testID }) => {
  const { isFullScreenMode } = useContext(FullScreenModeContext);

  // An expanded panel puts this row a few pixels below the top of the screen,
  // where a tooltip above it does not fit. antd would normally flip it, but the
  // overlay is fixed and the check behind that runs against the scrolled page.
  return (
    <CORETooltip
      position={isFullScreenMode ? "bottom" : "top"}
      width={"auto"}
      title={""}
      message={action.tooltip}
      testID={`${testID}${action.tooltipTestIDSuffix}`}
    >
      <COREButton
        type={"default"}
        size={"md"}
        icon={<COREIcon icon={action.icon} />}
        onClick={action.onClick}
        to={action.to}
        testID={`${testID}${action.buttonTestIDSuffix}`}
      />
    </CORETooltip>
  );
};

export const toInteractionButton = (
  action: HeaderChartAction,
  testID: TestID
): InteractionProps => (
  <ChartActionButton key={action.key} action={action} testID={testID} />
);

export const toMenuItem = (
  action: ChartAction,
  testID: TestID
): DropdownMenuItem => ({
  component: (
    <COREMenuItem
      key={action.key}
      testID={`${testID}${action.menuTestIDSuffix}`}
      icon={<COREIcon icon={action.icon} />}
      onClick={action.onClick}
      to={action.to}
    >
      {action.label}
    </COREMenuItem>
  ),
  closeWhenClick: true,
});

/**
 * The chart panel's own overflow dropdown.
 *
 * The container only reveals its dropdown once the header is too narrow for
 * the button row, so a chart that needs its overflow actions reachable at
 * every width has to bring its own. Rendering it as an ordinary header
 * interaction means the container hides it along with the buttons when it
 * collapses, leaving exactly one dropdown on screen at any width.
 */
export const toChartMenuButton = (
  actions: ChartAction[],
  testID: TestID,
  /**
   * Rows a caller has already built, listed above the shared actions. A chart
   * with an action of its own — "Edit" on Contracted Capacity, say — has only
   * the container's collapsed menu to put it in otherwise, which leaves it
   * unreachable at every width that still fits the header.
   */
  extraItems: DropdownMenuItem[] = [],
  /** Put the caller's rows below the shared actions instead of above them. */
  extraItemsLast = false
): InteractionProps => (
  <COREDropdownMenu
    key={"chart-menu"}
    interactionsMenu={
      extraItemsLast
        ? [
            ...actions.map((action) => toMenuItem(action, testID)),
            ...extraItems,
          ]
        : [
            ...extraItems,
            ...actions.map((action) => toMenuItem(action, testID)),
          ]
    }
    testID={`${testID}-chart-menu`}
    isDisabled={false}
    placement={"bottomRight"}
    // The same class the container puts on its own dropdown, so both popups
    // are styled by the same rules.
    className={"accordion-dropdown-menu"}
  >
    <COREButton
      type={"default"}
      size={"md"}
      icon={<COREIcon icon={icon({ name: "ellipsis", style: "regular" })} />}
      testID={`${testID}-chart-menu-button`}
    />
  </COREDropdownMenu>
);
